import { Link } from "react-router-dom";
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  MapPin,
  UserRoundCog,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  avatarHue,
  departmentLabel,
  designationLabel,
  employeeDisplayNameFormatted,
  formatProfileDate,
  formatStatusLabel,
  getInitials,
  locationLabel,
  managerLabel,
} from "../employeeProfileUtils";
import {
  canDeactivateEmployee,
  canEditEmployee,
  canExportProfile,
  canManageSalary,
} from "../employeeProfilePermissions";

function StatusPill({ status }) {
  const normalized = String(status || "unknown").toLowerCase();
  return (
    <span className={`ep-status-pill ep-status-pill--${normalized}`}>
      <span className="ep-status-dot" aria-hidden />
      {formatStatusLabel(status)}
    </span>
  );
}

export default function EmployeeProfileHeader({ employee, role, onEdit, onExport }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const name = employeeDisplayNameFormatted(employee);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPos(null);
      return undefined;
    }
    const place = () => {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight || 280;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < Math.min(panelHeight, 220) && rect.top > spaceBelow;
      const width = Math.max(panelRef.current?.offsetWidth || 260, 260);
      setMenuPos({
        top: openUp ? Math.max(8, rect.top - Math.min(panelHeight, rect.top - 8) - 8) : rect.bottom + 8,
        left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
        maxHeight: Math.max(160, openUp ? rect.top - 16 : spaceBelow),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const showEdit = canEditEmployee(role);
  const exited = ["terminated", "inactive", "exited"].includes(
    String(employee?.status || "").toLowerCase()
  );

  return (
    <header className="ep-header">
      <div className="ep-topbar">
        <nav className="ep-breadcrumb" aria-label="Breadcrumb">
          <Link to="/employees">People Hub</Link>
          <ChevronRight size={13} aria-hidden />
          <Link to="/employees">Employees</Link>
          <ChevronRight size={13} aria-hidden />
          <span aria-current="page">{name || "Employee"}</span>
        </nav>
      </div>

      <div className="ep-hero">
        <div className="ep-hero-identity">
          <div className="ep-avatar" style={{ background: avatarHue(name) }} aria-hidden>
            {getInitials(employee)}
          </div>
          <div className="ep-header-meta">
            <h1 className="ep-name">{name || "—"}</h1>
            <div className="ep-title-row">
              <span className="ep-code-badge">{employee?.employee_code || "—"}</span>
              <span className="ep-role-line">
                {designationLabel(employee)}
                <span className="ep-dot" aria-hidden>•</span>
                {departmentLabel(employee)}
              </span>
            </div>
            <div className="ep-header-tags">
              <StatusPill status={employee?.status} />
              {employee?.employee_type ? <span className="ep-tag">{employee.employee_type}</span> : null}
            </div>
          </div>
        </div>

        <dl className="ep-header-facts">
          <div className="ep-fact">
            <CalendarDays size={20} strokeWidth={1.8} aria-hidden />
            <div>
              <dt>Joining Date</dt>
              <dd>{formatProfileDate(employee?.date_of_joining)}</dd>
            </div>
          </div>
          <div className="ep-fact">
            <MapPin size={20} strokeWidth={1.8} aria-hidden />
            <div>
              <dt>Location</dt>
              <dd>{locationLabel(employee)}</dd>
            </div>
          </div>
          <div className="ep-fact">
            <UserRoundCog size={20} strokeWidth={1.8} aria-hidden />
            <div>
              <dt>Reporting Manager</dt>
              <dd>{managerLabel(employee)}</dd>
            </div>
          </div>
        </dl>

        <div className="ep-header-actions">
          <div className="ep-menu-wrap" ref={menuRef}>
            <button
              ref={buttonRef}
              type="button"
              className="ep-btn ep-btn-primary"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
            >
              More Actions
              <ChevronDown size={16} />
            </button>
              {menuOpen
                ? createPortal(
                <div
                  className="ep-menu"
                  role="menu"
                  ref={panelRef}
                  style={
                    menuPos
                      ? { top: menuPos.top, left: menuPos.left, maxHeight: menuPos.maxHeight }
                      : { visibility: "hidden" }
                  }
                >
                  {showEdit ? (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onEdit();
                      }}
                    >
                      Edit Employee
                    </button>
                  ) : null}
                  {canManageSalary(role) ? (
                    <Link
                      role="menuitem"
                      to={`/employeesalarystructure?employee_id=${encodeURIComponent(employee?.employee_id || "")}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      Assign Salary Package
                    </Link>
                  ) : null}
                  {showEdit ? (
                    <Link
                      role="menuitem"
                      to={`/employees/${employee?.employee_id}/edit`}
                      onClick={() => setMenuOpen(false)}
                    >
                      Change Department / Designation
                    </Link>
                  ) : null}
                  <Link role="menuitem" to="/org-hierarchy" onClick={() => setMenuOpen(false)}>
                    Change Reporting Manager
                  </Link>
                  {canExportProfile(role) ? (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onExport?.();
                      }}
                    >
                      Export Employee Profile
                    </button>
                  ) : null}
                  {canDeactivateEmployee(role) && !exited ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="ep-menu-danger"
                      disabled
                      title="Use Employee Lifecycle for exit processing"
                    >
                      Deactivate Employee
                    </button>
                  ) : null}
                </div>,
                document.body
              )
                : null}
          </div>
        </div>
      </div>
    </header>
  );
}
